import { type UseMutationResult, useMutation } from '@tanstack/react-query';

import api from '@/api/axios';
import { queryClient } from '@/app/config/react-query';
import { useErrorHandling } from '@/hooks/useErrorHandling';
import { useAuditStore } from '@/store/auditStore';
import type { DeleteDocumentParams } from '@/types/documentUpload';

const deleteAuditCaseDocument = async ({
  auditCaseUuid,
  documentUuid,
}: DeleteDocumentParams): Promise<void> => {
  await api.delete(`/audit-cases/${auditCaseUuid}/documents/${documentUuid}`);
};

export const useDeleteAuditCaseDocument = (): UseMutationResult<
  void,
  unknown,
  DeleteDocumentParams
> => {
  const handleErrors = useErrorHandling();
  const incrementPendingActions = useAuditStore((state) => state.incrementPendingActions);
  const decrementPendingActions = useAuditStore((state) => state.decrementPendingActions);

  return useMutation({
    mutationFn: deleteAuditCaseDocument,
    onMutate: () => incrementPendingActions(),
    onSuccess: (_, { auditCaseUuid }) => {
      queryClient.invalidateQueries({
        queryKey: ['audit-case-documents', auditCaseUuid],
      });
      queryClient.invalidateQueries({
        queryKey: ['audit-case-document-definitions', auditCaseUuid],
      });
    },
    onError: (error: unknown) => {
      handleErrors(error);
    },
    onSettled: () => decrementPendingActions(),
  });
};
